import { useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { BubbleMenu } from '@tiptap/react';
import { Editor } from '@tiptap/core';
// bare import: registers the setLink/unsetLink command types on Editor
import '@tiptap/extension-link';
import { ReactPageClick } from 'react-page-click';

import { KEYS } from 'components/utils/keys';
import { Tooltip } from 'components/overlay/Tooltip';
import { IconName } from 'components/elements/types';
import { linkRegex } from 'utils/regexes';

import * as styled from '../styles';
import i18n from '../utils/i18n';
import { applyBubbleBoundary, bubblePopperOptions } from '../utils';

const normalizeHref = (raw: string): string | null => {
  const value = raw.trim();
  if (!value) return null;

  if (/^tel:\+?[\d\s().-]+$/i.test(value)) return value;

  const href = /^(https?:\/\/|mailto:)/i.test(value)
    ? value
    : `https://${value}`;

  return linkRegex.test(href) ? href : null;
};

const selectedUrlText = (editor: Editor): string => {
  const { empty, from, to } = editor.state.selection;
  if (empty) return '';

  const text = editor.state.doc.textBetween(from, to, ' ').trim();
  if (!text || /\s/.test(text)) return '';

  return normalizeHref(text) ? text : '';
};

const applyHref = (editor: Editor, raw: string) => {
  const href = normalizeHref(raw);

  if (!href) {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }

  if (editor.state.selection.empty && !editor.isActive('link')) {
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'text',
        text: href,
        marks: [{ type: 'link', attrs: { href } }],
      })
      .run();
    return;
  }

  editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
};

type LinkFormProps = {
  editor: Editor;
  onDone: () => void;
};

function LinkForm({ editor, onDone }: LinkFormProps) {
  const intl = useIntl();
  const [value, setValue] = useState<string>(
    () => editor.getAttributes('link').href || selectedUrlText(editor)
  );
  const [invalid, setInvalid] = useState(false);

  const submit = useCallback(() => {
    const trimmed = value.trim();

    if (trimmed && !normalizeHref(trimmed)) {
      setInvalid(true);
      return;
    }

    applyHref(editor, trimmed);
    onDone();
  }, [editor, value, onDone]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.keyCode === KEYS.ENTER) {
        event.preventDefault();
        submit();
      }
      if (event.keyCode === KEYS.ESCAPE) onDone();
    },
    [submit, onDone]
  );

  return (
    <styled.LinkForm>
      <styled.LinkInput
        autoFocus
        value={value}
        invalid={invalid}
        placeholder={intl.formatMessage(i18n.linkPlaceholder)}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setValue(event.target.value);
          setInvalid(false);
        }}
        onKeyDown={handleKeyDown}
      />
      <Tooltip
        color="dark"
        fixed
        position="S"
        content={intl.formatMessage(i18n.linkInsert)}
        padding={0}
      >
        <span
          role="presentation"
          onMouseDown={(event: React.MouseEvent) => {
            event.preventDefault();
            submit();
          }}
        >
          <styled.MarkButton active={false}>
            <styled.Icon icon="Check" weight="bold" />
          </styled.MarkButton>
        </span>
      </Tooltip>
    </styled.LinkForm>
  );
}

type LinkToolbarButtonProps = {
  editor: Editor | null;
};

export function LinkToolbarButton({ editor }: LinkToolbarButtonProps) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  if (!editor) return null;

  return (
    <ReactPageClick notify={() => setOpen(false)}>
      <styled.DropdownWrapper>
        <Tooltip
          color="dark"
          fixed
          position="S"
          content={intl.formatMessage(i18n.linkInsert)}
          padding={0}
        >
          <styled.MarkButton
            active={editor.isActive('link')}
            onClick={() => setOpen(!open)}
          >
            <styled.Icon icon="LinkSimple" weight="bold" />
          </styled.MarkButton>
        </Tooltip>
        {open && (
          <styled.LinkPopover>
            <LinkForm editor={editor} onDone={() => setOpen(false)} />
          </styled.LinkPopover>
        )}
      </styled.DropdownWrapper>
    </ReactPageClick>
  );
}

type LinkActionProps = {
  message: I18nMessage;
  icon: IconName;
  danger?: boolean;
  onAction: () => void;
};

function LinkAction({ message, icon, danger, onAction }: LinkActionProps) {
  const intl = useIntl();

  return (
    <Tooltip
      color="dark"
      fixed
      position="S"
      content={intl.formatMessage(message)}
      padding={0}
    >
      <span
        role="presentation"
        onMouseDown={(event: React.MouseEvent) => {
          event.preventDefault();
          onAction();
        }}
      >
        <styled.TableToolButton active={false} danger={danger}>
          <styled.Icon icon={icon} weight="bold" />
        </styled.TableToolButton>
      </span>
    </Tooltip>
  );
}

type LinkToolsProps = {
  editor: Editor | null;
};

export function LinkTools({ editor }: LinkToolsProps) {
  const [editing, setEditing] = useState(false);

  if (!editor) return null;

  return (
    <BubbleMenu
      pluginKey="linkBubbleMenu"
      className="tooltip-shadow"
      editor={editor}
      tippyOptions={{
        duration: 100,
        placement: 'bottom',
        popperOptions: bubblePopperOptions(editor),
        onShow: applyBubbleBoundary(editor),
        onHidden: () => setEditing(false),
      }}
      shouldShow={({ editor: currentEditor }) =>
        currentEditor.isEditable && currentEditor.isActive('link')
      }
    >
      {editing ? (
        <LinkForm
          key={editor.state.selection.from}
          editor={editor}
          onDone={() => setEditing(false)}
        />
      ) : (
        <styled.TableToolsWrapper>
          <LinkAction
            message={i18n.linkOpen}
            icon="ArrowSquareOut"
            onAction={() => {
              const { href } = editor.getAttributes('link');
              if (href) window.open(href, '_blank', 'noopener,noreferrer');
            }}
          />
          <LinkAction
            message={i18n.linkEdit}
            icon="PencilSimple"
            onAction={() => setEditing(true)}
          />
          <LinkAction
            message={i18n.linkRemove}
            icon="LinkBreak"
            danger
            onAction={() =>
              editor.chain().focus().extendMarkRange('link').unsetLink().run()
            }
          />
        </styled.TableToolsWrapper>
      )}
    </BubbleMenu>
  );
}
